import React, { useEffect, useState, useCallback } from 'react';
import { employeeAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Users, Plus, Edit2 } from 'lucide-react';

export default function EmployeesPage() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', password: '', role: 'staff',
    department: '', phone: '', employeeId: '', expenseLimit: '5000'
  });

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await employeeAPI.list();
      setEmployees(res.data.employees);
    } catch (err) { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email required'); return; }
    try {
      const payload = { ...form, expenseLimit: parseFloat(form.expenseLimit) || 5000 };
      if (editId) {
        if (!payload.password) delete payload.password;
        delete payload.email;
        await employeeAPI.update(editId, payload);
        toast.success('Employee updated');
      } else {
        if (!payload.password) { toast.error('Password required for new employee'); return; }
        await employeeAPI.create(payload);
        toast.success('Employee created');
      }
      resetForm();
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const startEdit = (emp) => {
    setEditId(emp.id);
    setForm({
      name: emp.name, email: emp.email, password: '', role: emp.role,
      department: emp.department || '', phone: emp.phone || '',
      employeeId: emp.employeeId || '', expenseLimit: String(emp.expenseLimit || 5000)
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditId(null);
    setForm({ name: '', email: '', password: '', role: 'staff', department: '', phone: '', employeeId: '', expenseLimit: '5000' });
    setShowForm(false);
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-4" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-700" />
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Employees</h1>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="add-employee-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Employee
          </Button>
        )}
      </div>

      {showForm && isAdmin && (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="employee-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="employee-name-input" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1" disabled={!!editId} data-testid="employee-email-input" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{editId ? 'New Password (optional)' : 'Password *'}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} className="mt-1" data-testid="employee-password-input" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="mt-1" data-testid="employee-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Department</Label>
              <Input value={form.department} onChange={(e) => setForm(f => ({ ...f, department: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Expense Limit (₹)</Label>
              <Input type="number" value={form.expenseLimit} onChange={(e) => setForm(f => ({ ...f, expenseLimit: e.target.value }))} className="mt-1" data-testid="employee-limit-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="save-employee-btn">
              {editId ? 'Update' : 'Create'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={resetForm} className="text-xs">Cancel</Button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Name</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Role</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase hidden md:table-cell">Expense Limit</th>
                <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase hidden md:table-cell">Advance Balance</th>
                <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Status</th>
                {isAdmin && <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-slate-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => (
                <tr key={emp.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`employee-row-${emp.email}`}>
                  <td className="px-4 py-2.5">
                    <p className="text-xs font-medium text-slate-900">{emp.name}</p>
                    <p className="text-[10px] text-slate-400 sm:hidden">{emp.email}</p>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 hidden sm:table-cell">{emp.email}</td>
                  <td className="px-4 py-2.5">
                    <RoleBadge role={emp.role} />
                  </td>
                  <td className="px-4 py-2.5 text-xs text-right font-medium text-slate-900 hidden md:table-cell">₹{(emp.expenseLimit || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-2.5 text-xs text-right hidden md:table-cell">
                    <span className={emp.advanceBalance > 0 ? 'text-amber-600 font-medium' : 'text-slate-400'}>
                      ₹{(emp.advanceBalance || 0).toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`text-[10px] ${emp.isActive ? 'text-green-600' : 'text-red-500'}`}>
                      {emp.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => startEdit(emp)} className="p-1 hover:bg-slate-100 rounded" data-testid={`edit-employee-${emp.email}`}>
                        <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


const ROLE_STYLES = {
  admin: 'bg-slate-900 text-white border-slate-900',
  manager: 'bg-blue-50 text-blue-700 border-blue-200',
  staff: 'bg-slate-50 text-slate-600 border-slate-200',
};

function RoleBadge({ role }) {
  return (
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${ROLE_STYLES[role] || ROLE_STYLES.staff}`}>
      {role}
    </span>
  );
}
